import React, { useState, useEffect } from 'react';
import { Modal, Form } from 'react-bootstrap'
import { Field, ErrorMessage, Formik } from 'formik';
import { toast } from 'react-toastify'
import PlacesAutocomplete, { geocodeByAddress, getLatLng, } from 'react-places-autocomplete';
import { ShippingAddress } from '../pages/commonValidation';
import useAxiosInstance from '../../Api/AxiosInstance';

/* ---------------------------------------------------------------------------
   NEW: Address Details popup.
   This is a COPY of the "Address Details" Formik form that already lives inside
   Checkout.js (the <Modal show={show} ...> block near the end of that file).
   It is copied rather than extracted so that Checkout.js - which is the live
   payment path - cannot regress. If you change the address form in one place,
   change it in the other.

   Used by CheckoutPopup.js via the "+ Add New Address" and edit (pencil) buttons.
   Saves with the same 'add-user-address' endpoint Checkout.js uses, then asks the
   parent to re-fetch the address list through onSaved().
--------------------------------------------------------------------------- */
function AddressFormModal({ show, onHide, selectedAddress, user, countries, states, currentCountry, setCurrentCountry, onSaved }) {

  const AxiosInstance = useAxiosInstance()

  // Local to this popup - Checkout.js keeps these on the page instead.
  const [coordinates, setCoordinates] = useState({ lat: null, lng: null })
  const [locationError, setLocationError] = useState(false)
  const [error, setError] = useState('')
  const [geoaddress, setGeoaddress] = useState('')

  /* ---------------------------------------------------------------------
     GOOGLE PLACES READINESS (added with the deferred Maps script).
     Same guard as Checkout.js, for the same reason: public/index.html now loads
     the Maps JS API with defer+async and CRA emits the React bundle as defer
     BEFORE it, so React renders while window.google is still undefined.
     react-places-autocomplete throws from componentDidMount in that state, so the
     Location field is not rendered until the library is present.
     Nothing else about this form changes.
  --------------------------------------------------------------------- */
  const [placesReady, setPlacesReady] = useState(
    () => !!(typeof window !== 'undefined' && window.google && window.google.maps && window.google.maps.places)
  );
  useEffect(() => {
    if (placesReady) return;
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      if (window.google && window.google.maps && window.google.maps.places) {
        setPlacesReady(true);
        clearInterval(timer);
      } else if (tries >= 100) {
        clearInterval(timer);
      }
    }, 100);
    return () => clearInterval(timer);
  }, [placesReady]);

  // Verbatim from Checkout.js handleSelect.
  const handleSelect = async (value, setFieldValue) => {
    try {
      const result = await geocodeByAddress(value);
      const ll = await getLatLng(result[0]);
      if (ll.lat == '' || ll.lng == '') {
        setLocationError(true)
      }
      setFieldValue('location', value);
      setFieldValue('lat', ll.lat)
      setFieldValue('lng', ll.lng)
      setGeoaddress(value);
      setCoordinates(ll);
    } catch (error) {
      console.error('Error selecting address:', error);
      setError('Error fetching location details');
    }
  };

  // Verbatim from Checkout.js handleUpdate, except the success path calls
  // onSaved() so the parent refreshes its address list and closes this modal.
  const handleUpdate = async (values) => {
    const isCountryMissing = !values.country_id;
    const isStateMissing = !values.state_id;
    const needsGeo = Number(values.country_id) !== 101;
    const isGeoMissing = needsGeo && (values.lat == null || values.lng == null);
    if ((isCountryMissing && isStateMissing) || isGeoMissing) {
      setLocationError(true);
      return;
    } else {
      try {
        const trimmedValues = Object.keys(values).reduce((acc, key) => {
          acc[key] = typeof values[key] === 'string' ? values[key].trim() : values[key];
          return acc;
        }, {});

        const response = await AxiosInstance.post('add-user-address', trimmedValues);
        if (response.data.success == 1) {
          toast.success('Address updated Successfully', {
            position: "top-right",
            autoClose: 1000,
          });
          setLocationError(false)
          onSaved();
        }
      } catch (error) {
        console.error("Error in handleUpdate:", error);
      }
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="md" centered>
      <div className='checkout-form'>
        <div className='white_box checkout-wrapper'>
          <h3 className='text-center'>Address Details</h3>
          <Formik
            initialValues={{
              id: selectedAddress ? selectedAddress.id : '',
              full_name: selectedAddress ? selectedAddress.full_name : user?.user?.full_name,
              mobile_no: selectedAddress ? selectedAddress.mobile_no : user?.user?.mobile_no,
              gst_no: selectedAddress ? selectedAddress.gst_no == "null" ? "" : selectedAddress.gst_no : '',
              permanent_address: selectedAddress ? selectedAddress.permanent_address : '',
              pincode: selectedAddress ? selectedAddress.pincode : '',
              location: selectedAddress ? selectedAddress.location : '',
              lat: selectedAddress ? selectedAddress.lat : '',
              lng: selectedAddress ? selectedAddress.lng : '',
              country_id: selectedAddress ? String(selectedAddress.country_id) : '101',
              state_id: selectedAddress && selectedAddress.state_id ? String(selectedAddress.state_id) : '',
            }}
            validationSchema={ShippingAddress}
            onSubmit={handleUpdate}
          >
            {({ handleSubmit, values, handleChange, setFieldValue }) => (
              <Form className='mt-4' onSubmit={handleSubmit}>
                <div className='form-inner d-flex'>
                  <div className='input-field full-width'>
                    <label className='label'>Full Name <em className='required'>*</em></label>
                    <Field type='text'
                      name="full_name"
                      placeholder="Enter Full Name"
                      value={values.full_name}
                      onChange={handleChange} />
                    <ErrorMessage name="full_name" >
                      {msg => (
                        <div className="alert alert-danger mt-3" role="alert">{msg}</div>
                      )}
                    </ErrorMessage>
                  </div>

                  <div className='input-field full-width'>
                    <label className='label'>Mobile no. <em className='required'>*</em></label>
                    <input type='text'
                      inputMode='numeric'
                      name="mobile_no"
                      placeholder="Enter Mobile No."
                      value={values.mobile_no}
                      onChange={handleChange}
                      onInput={(e) => {
                        e.target.value = e.target.value.replace(/\D/g, '');
                        e.target.value = e.target.value.slice(0, 10);
                      }} />
                    <ErrorMessage name="mobile_no" >
                      {msg => (
                        <div className="alert alert-danger mt-3" role="alert">{msg}</div>
                      )}
                    </ErrorMessage>
                  </div>

                  <div className='input-field full-width'>
                    <label className='label'>GSTIN Number (optional)</label>
                    <input type='text' name="gst_no"
                      placeholder='Enter GSTIN Number'
                      value={values.gst_no}
                      onChange={handleChange}
                      maxLength={15}
                    />
                    <ErrorMessage name="gst_no" >
                      {msg => (
                        <div className="alert alert-danger mt-3" role="alert">{msg}</div>
                      )}
                    </ErrorMessage>
                  </div>

                  <div className='input-field full-width'>
                    <label className='label'>Street Address <em className='required'>*</em></label>
                    <input type='text'
                      name="permanent_address"
                      placeholder='Enter Address'
                      value={values.permanent_address}
                      onChange={handleChange} />
                    <ErrorMessage name="permanent_address" >
                      {msg => (
                        <div className="alert alert-danger mt-3" role="alert">{msg}</div>
                      )}
                    </ErrorMessage>
                  </div>

                  <div className='input-field full-width'>
                    <label className='label'>Country <em className='required'>*</em></label>
                    <Field as="select" name="country_id" value={values.country_id} onChange={(e) => { handleChange(e); setFieldValue('state_id', ''); setCurrentCountry(e.target.value); }}>
                      <option value="">Select Country</option>
                      {countries && countries.map(country => {
                        const optVal = String(country.value ?? country.id ?? country.country_id ?? country.code ?? '');
                        const optLabel = country.label ?? country.name ?? country.title ?? '';
                        return (
                          <option key={optVal} value={optVal}>{optLabel}</option>
                        )
                      })}
                    </Field>
                    <ErrorMessage name="country_id" >
                      {msg => (
                        <div className="alert alert-danger mt-3" role="alert">{msg}</div>
                      )}
                    </ErrorMessage>
                  </div>

                  {values.country_id === '101' ? (
                    <div className='input-field full-width'>
                      <label className='label'>State <em className='required'>*</em></label>
                      <Field as="select" name="state_id" value={values.state_id} onChange={handleChange}>
                        <option value="">Select State</option>
                        {states && states.map(state => {
                          const optVal = String(state.value ?? state.id ?? state.state_id ?? '');
                          const optLabel = state.label ?? state.name ?? state.title ?? '';
                          return (
                            <option key={optVal} value={optVal}>{optLabel}</option>
                          )
                        })}
                      </Field>
                      <ErrorMessage name="state_id" >
                        {msg => (
                          <div className="alert alert-danger mt-3" role="alert">{msg}</div>
                        )}
                      </ErrorMessage>
                    </div>
                  ) : (<> </>)}

                  {values.country_id !== '101' ? (
                    <div className='input-field full-width'>
                      <label className='label'>Location <em className='required'>*</em></label>
                      {/* placesReady gate - see the state block above. */}
                      {placesReady ?
                      <PlacesAutocomplete
                        value={values.location}
                        onChange={(address) => { setFieldValue('location', address); setFieldValue('lat', ''); setFieldValue('lng', ''); setCoordinates({ lat: null, lng: null }); setLocationError(false) }}
                        onSelect={(address) => handleSelect(address, setFieldValue)}
                      >
                        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                          <div key={suggestions.description}>
                            <input
                              {...getInputProps({
                                placeholder: 'Search Places ...',
                                className: 'location-search-input',
                              })}
                            />
                            <div className="autocomplete-dropdown-container">
                              {loading && <div>Loading...</div>}
                              {suggestions?.map(suggestion => {
                                return (
                                  <ul
                                    {...getSuggestionItemProps(suggestion, {
                                    })}
                                  >
                                    <li key={suggestion.id}>{suggestion.description}</li>
                                  </ul>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </PlacesAutocomplete>
                      : (<> </>)}
                      {locationError ?
                        <div className="alert alert-danger mt-3" role="alert">Please select valid address</div>
                        : ""}
                      <ErrorMessage name="location" >
                        {msg => (
                          <div className="alert alert-danger mt-3" role="alert">{msg}</div>
                        )}
                      </ErrorMessage>
                    </div>
                  ) : (<> </>)}

                  <div className='input-field full-width'>
                    <label className='label'>Pin Code <em className='required'>*</em></label>
                    <input type='text'
                      name="pincode"
                      placeholder='Enter Pin Code'
                      value={values.pincode}
                      onChange={handleChange}
                      maxLength={6} />
                    <ErrorMessage name="pincode" >
                      {msg => (
                        <div className="alert alert-danger mt-3" role="alert">{msg}</div>
                      )}
                    </ErrorMessage>
                  </div>

                  <div className='input-field full-width form-btnbox mb-0 pt-2 d-flex'>
                    <button className='btn secondary ml-auto' type="submit">Submit</button>
                    <button type='button' className='btn primary ml-2' onClick={onHide}>Cancel</button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </Modal>
  )
}

export default AddressFormModal
